import { AnimatePresence, motion } from 'motion/react';
import { memo, useEffect, useRef } from 'react';

import { cn } from '@infinex/ui-styles';
import { arePropsEqual } from '@infinex/web-shared/utils';

import { AnimatedDigits } from './AnimatedDigits';
import { AsteriskNumber } from './AsteriskNumber';
import {
  formatNumberByOptions,
  normalizeSuffix,
  shouldUseZeroIndexNotation,
} from './utils';

/**
 * 🔢 A number formatting component that handles both standard and very small numbers.
 *
 * Uses two distinct formats for improved readability:
 *
 * 1. Standard Format: For regular numbers and decimals up to 5 places
 *    e.g. "123.45", "1,234.56", "0.00892"
 *
 * 2. Zero-Index Notation: For very small numbers (6+ leading zeros)
 *    e.g. "0.0[6]789" represents 0.000000789
 *    This format is more intuitive than scientific notation for non-technical users
 */
interface FormattedNumberProps {
  /** The prefix to display before the number,
   * for example "$", when use with `withAnimation`,
   * the prefix should be passed in so that the line height can match,
   * it works with `withAnimation` false as well
   * */
  prefix?: string;
  /** The suffix to display after the number,
   * for example " with NFTs", when use with `withAnimation`,
   * the suffix should be passed in so that the line height can match
   * it works with `withAnimation` false as well
   * */
  suffix?: string;
  /** The number to format */
  value: number;
  /** Removes decimal places entirely */
  roundToWholeNumber?: boolean;
  /** Forces exact decimal places, overriding smart decimal handling */
  decimals?: number;
  /** Takes precedence over decimals if both specified */
  significantFigures?: number;
  /** When true, removes trailing zeros in decimals */
  trimTrailingZeros?: boolean;
  /** If true, the number will be animated */
  withAnimation?: boolean;
  /** If true, the number will be hidden */
  obfuscate?: boolean;
  /** Options for obfuscation behavior */
  obfuscationOptions?: {
    /** The length of the obfuscated number (for asterisk variant) */
    length?: number;
    /** The animation variant: 'asterisk' shows asterisks, 'remove' collapses width */
    variant?: 'asterisk' | 'remove';
  };

  strikeThrough?: boolean;
  /** Optional class for the decimal part (e.g. "text-secondary") */
  decimalPartClassName?: string;
}

export const FormattedNumber = memo(FormattedNumberComponent, arePropsEqual);

function FormattedNumberComponent({
  prefix = '',
  suffix = '',
  value,
  decimals,
  significantFigures,
  roundToWholeNumber = false,
  trimTrailingZeros = false,
  withAnimation = false,
  obfuscate = false,
  obfuscationOptions = {
    length: 5,
    variant: 'asterisk',
  },
  strikeThrough = false,
  decimalPartClassName,
}: FormattedNumberProps) {
  const formatResult = formatNumberByOptions(value, {
    roundToWholeNumber,
    decimals,
    significantFigures,
    trimTrailingZeros,
  });

  const useZeroIndexNotation = shouldUseZeroIndexNotation(formatResult);

  // Normalize suffix to auto-add space for alphanumeric suffixes
  const normalizedSuffix = normalizeSuffix(suffix);

  // Track if this is the first render and previous obfuscate state
  const isFirstRender = useRef(true);
  const prevObfuscate = useRef(obfuscate);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else {
      prevObfuscate.current = obfuscate;
    }
  }, [obfuscate]);

  // Render function for the actual number content
  const renderNumberContent = () => {
    if (useZeroIndexNotation && formatResult.zeroIndexNotation) {
      const {
        prefix: notationPrefix,
        truncatedZeroCount,
        significantDigits,
      } = formatResult.zeroIndexNotation;

      return (
        <div className="inline-flex items-end tabular-nums">
          <AnimatedDigits
            withAnimation={withAnimation}
            prefix={prefix}
            className={cn(strikeThrough ? 'line-through' : undefined)}
          >
            {notationPrefix}
          </AnimatedDigits>
          <AnimatedDigits
            withAnimation={withAnimation}
            className={cn(
              'mx-0.5 inline-flex',
              strikeThrough ? 'line-through' : undefined
            )}
            style={{
              fontSize: '0.75em',
              marginBottom: '-0.35em',
            }}
          >
            {truncatedZeroCount.toString()}
          </AnimatedDigits>
          <AnimatedDigits
            withAnimation={withAnimation}
            suffix={normalizedSuffix}
            className={cn(strikeThrough ? 'line-through' : undefined)}
          >
            {significantDigits}
          </AnimatedDigits>
        </div>
      );
    }

    const realNotation = formatResult.realNotation;
    const dotIndex = realNotation.indexOf('.');
    const hasDecimalPart =
      dotIndex !== -1 && dotIndex < realNotation.length - 1;

    if (hasDecimalPart && decimalPartClassName && value > 1) {
      return (
        <span className="inline-flex items-end whitespace-nowrap tabular-nums">
          <AnimatedDigits
            withAnimation={withAnimation}
            className={cn(strikeThrough ? 'line-through' : undefined)}
            prefix={prefix}
          >
            {realNotation.slice(0, dotIndex)}
          </AnimatedDigits>

          <AnimatedDigits
            withAnimation={withAnimation}
            className={cn(
              decimalPartClassName,
              strikeThrough ? 'line-through' : undefined
            )}
            suffix={normalizedSuffix}
          >
            {'.' + realNotation.slice(dotIndex + 1)}
          </AnimatedDigits>
        </span>
      );
    }

    return (
      <AnimatedDigits
        withAnimation={withAnimation}
        className={cn(
          strikeThrough ? 'line-through' : undefined,
          'whitespace-nowrap tabular-nums'
        )}
        prefix={prefix}
        suffix={normalizedSuffix}
      >
        {formatResult.realNotation}
      </AnimatedDigits>
    );
  };

  // Handle "remove" variant with collapse/expand animation
  if (obfuscationOptions.variant === 'remove') {
    return (
      <motion.span
        className="inline-flex items-center"
        initial={false}
        animate={{
          marginLeft: obfuscate ? '-4px' : 0,
        }}
        transition={{
          marginLeft: {
            type: 'spring',
            stiffness: 400,
            damping: 35,
          },
        }}
      >
        <motion.span
          className="inline-block overflow-hidden"
          initial={false}
          animate={{
            width: obfuscate ? 0 : 'auto',
            scaleX: obfuscate ? 0 : 1,
            opacity: obfuscate ? 0 : 1,
          }}
          transition={{
            width: obfuscate
              ? {
                  type: 'spring',
                  stiffness: 400,
                  damping: 35,
                }
              : {
                  type: 'spring',
                  stiffness: 350,
                  damping: 25,
                  bounce: 0.3,
                },
            scaleX: obfuscate
              ? {
                  type: 'spring',
                  stiffness: 350,
                  damping: 30,
                }
              : {
                  type: 'spring',
                  stiffness: 300,
                  damping: 20,
                  bounce: 0.4,
                },
            opacity: { duration: 0.15 },
          }}
          style={{
            transformOrigin: 'center',
            verticalAlign: 'baseline',
          }}
        >
          {renderNumberContent()}
        </motion.span>
      </motion.span>
    );
  }

  // Handle "asterisk" variant (default) with scrolling animation
  return (
    <span className="relative inline-block">
      {/* Invisible spacer to maintain height */}
      <span className="invisible" aria-hidden="true">
        {obfuscate ? (
          <AsteriskNumber
            length={obfuscationOptions.length}
            prefix={prefix}
            suffix={normalizedSuffix}
          />
        ) : (
          // Use a placeholder string to maintain layout without duplicating content
          // This prevents test failures from seeing doubled content
          // Using a space maintains layout without showing misleading content
          <span className="tabular-nums" style={{ visibility: 'hidden' }}>
            {/* Render the actual content but make it invisible */}
            {renderNumberContent()}
          </span>
        )}
      </span>

      <AnimatePresence initial={false}>
        {obfuscate ? (
          <motion.span
            key="asterisks"
            className="absolute inset-0 inline-block"
            initial={isFirstRender.current ? false : { y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -20, opacity: 0 }}
            transition={{
              type: 'spring',
              stiffness: 350,
              damping: 30,
            }}
          >
            <AsteriskNumber
              length={obfuscationOptions.length}
              prefix={prefix}
              suffix={normalizedSuffix}
            />
          </motion.span>
        ) : (
          <motion.span
            key="number"
            className="absolute inset-0 inline-block"
            initial={isFirstRender.current ? false : { y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -20, opacity: 0 }}
            transition={{
              type: 'spring',
              stiffness: 350,
              damping: 30,
            }}
          >
            {renderNumberContent()}
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  );
}
